import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { Layout } from './components/Layout/Layout';
import { Login } from './pages/Login';
import { Security } from './pages/Security';
import { Privacy } from './pages/Privacy';
import { Terms } from './pages/Terms';
import { Dashboard } from './pages/Dashboard';
import { Integrators } from './pages/Integrators';
import { IntegratorDetail } from './pages/IntegratorDetail';
import { Customers } from './pages/Customers';
import { CustomerDetail } from './pages/CustomerDetail';
import { Properties } from './pages/Properties';
import { PropertyDetail } from './pages/PropertyDetail';
import { Tenants } from './pages/Tenants';
import { TenantDetail } from './pages/TenantDetail';
import { Devices } from './pages/Devices';
import { AccessControl } from './pages/AccessControl';
import { AccessControlSystemDetail } from './pages/AccessControlSystemDetail';
import { Cameras } from './pages/Cameras';
import { CameraSystemDetail } from './pages/CameraSystemDetail';
import PushSender from './pages/PushSender';
import { Intercoms } from './pages/Intercoms';
import { Accessories } from './pages/Accessories';
import { Notifications as NotificationsPage } from './pages/Notifications';
import { Settings } from './pages/Settings';
import { AdminDashboard } from './pages/AdminDashboard';
import { BillingRatesAdmin } from './pages/admin/BillingRatesAdmin';
import { CellularAdmin } from './pages/admin/CellularAdmin';
import { AdminInbox } from './pages/AdminInbox';
import { Billing } from './pages/Billing';
import { VoiceSettings } from './pages/VoiceSettings';
import { SignShop } from './pages/SignShop';
import { VistaprintShop } from './pages/VistaprintShop';
import { SignShopOrders } from './pages/SignShopOrders';
import { AccessoryShopOrders } from './pages/AccessoryShopOrders';
import { DeviceProfilesAdmin } from './pages/DeviceProfilesAdmin';
import { ClaimBridge } from './pages/ClaimBridge';
import { VisitorDirectory, VisitorCall, VisitorDemo } from './pages/visitor';
import { TenantPortal } from './pages/TenantPortal';
import { VisitorPassPortal } from './pages/VisitorPassPortal';
import { TenantVideoCall } from './pages/visitor/TenantVideoCall';
import { WebUsers } from './pages/WebUsers';
import { MobileUsers } from './pages/MobileUsers';
import { CallHistory } from './pages/CallHistory';
import { DeviceHistory } from './pages/DeviceHistory';
import { Reports } from './pages/Reports';
import { Guests } from './pages/Guests';
import { Deliveries } from './pages/Deliveries';
import { Vendors } from './pages/Vendors';
import { Integrations } from './pages/Integrations';
import { Rules } from './pages/Rules';
import { Permissions } from './pages/Permissions';
import { Analytics } from './pages/Analytics';
import { Operations } from './pages/Operations';
import { AdminEntityDetail } from './pages/AdminEntityDetail';
import { Metrics } from './pages/Metrics';
import { EventHistory } from './pages/EventHistory';
import { StripePage } from './pages/Stripe';
import { IntrlockBridge } from './pages/IntrlockBridge';
import { AdminDataBrowser } from './pages/AdminDataBrowser';
import { LiveVisitorPages } from './pages/LiveVisitorPages';
import { BetaVisitorDirectory } from './pages/BetaVisitorDirectory';
import { PermissionsProvider } from './context/PermissionsContext';
import {
  canViewIntegrators,
  canViewCustomers,
  canViewProperties,
  canViewTenants,
  canViewDevices,
  canViewAccessControl,
  canViewBilling,
  canViewVoice,
  canViewSettings,
  canViewWebUsers,
  canViewMobileUsers,
  canViewCallHistory,
  canViewGuests,
  canViewCameras,
  canViewIntercoms,
  canViewPermissions,
  canAccessSignShop,
  isSuperAdmin,
} from './utils/roles';

import { initAnalytics, trackPageView } from './utils/analytics';
import { useLocation } from 'react-router-dom';
import { IS_TENANT_SURFACE, IS_VISITOR_SURFACE, IS_VISITOR_PASS_SURFACE, visitorOrigin } from './utils/surface';

// Analytics: track page views on route change. Skip visitor-domain and
// `/visit/*` paths — those use trackVisitorView() which stamps property_id +
// intercom_config_id, and double-firing would inflate visitor_page_views.
const AnalyticsTracker: React.FC = () => {
  const location = useLocation();
  React.useEffect(() => { initAnalytics(); }, []);
  React.useEffect(() => {
    const isVisitor = IS_VISITOR_SURFACE
      || location.pathname.startsWith('/visit')
      || /^\/[0-9a-f-]{36}(\/i\/[0-9a-f-]+)?$/i.test(location.pathname);
    if (isVisitor) return;
    trackPageView(location.pathname);
  }, [location.pathname]);
  return null;
};

const isVisitorDomain = IS_VISITOR_SURFACE;
const isTenantDomain = IS_TENANT_SURFACE;
const isVisitorPassDomain = IS_VISITOR_PASS_SURFACE;

const VisitorPassApp: React.FC = () => (
  <BrowserRouter><Routes><Route path="/p/:publicId" element={<VisitorPassPortal />} /><Route path="*" element={<VisitorPassPortal />} /></Routes></BrowserRouter>
);

// Redirect old /visit/* URLs to visitor.intrlock.io
const VisitRedirect: React.FC = () => {
  const path = window.location.pathname.replace('/visit', '');
  window.location.href = `${visitorOrigin()}${path || '/'}`;
  return null;
};

// Visitor-only app for visitor.intrlock.io
const VisitorApp: React.FC = () => (
  <BrowserRouter>
    <AnalyticsTracker />
    <Routes>
      <Route path="/demo" element={<VisitorDemo />} />
      {/* Per-intercom routes — same directory, intercom-specific door scope */}
      <Route path="/:propertyId/i/:intercomId/call/:unitNumber" element={<VisitorCall />} />
      <Route path="/:propertyId/i/:intercomId" element={<VisitorDirectory />} />
      <Route path="/:propertyId/call/:unitNumber" element={<VisitorCall />} />
      <Route path="/:propertyId" element={<VisitorDirectory />} />
      <Route path="*" element={<VisitorDirectory />} />
    </Routes>
  </BrowserRouter>
);

// Tenant portal has no react-router, so wire analytics inline. Fires
// session_start + an initial page_view stamped with domain=tenants.intrlock.io
// so the admin Analytics dashboard sees tenant-portal traffic.
const TenantPortalWithAnalytics: React.FC = () => {
  React.useEffect(() => {
    initAnalytics();
    trackPageView(window.location.pathname || '/');
  }, []);
  return <TenantPortal />;
};

const App: React.FC = () => {
  if (isVisitorPassDomain) return <VisitorPassApp />;
  if (isVisitorDomain) return <VisitorApp />;
  if (isTenantDomain) return <TenantPortalWithAnalytics />;

  // Admin-only build: when bundled with REACT_APP_BUILD_TARGET=admin
  // (or served from admin.intrlock.io), only super-admin routes are
  // mounted. Everything else redirects to /admin so dealer / customer
  // / property-manager data can never spill into this surface even if
  // a logged-in user types a tenant URL by hand.
  const isAdminBuild =
    process.env.REACT_APP_BUILD_TARGET === 'admin'
    || (typeof window !== 'undefined' && window.location.hostname.startsWith('admin.'));

  if (isAdminBuild) {
    return (
      <AuthProvider>
        <BrowserRouter>
          <AnalyticsTracker />
          <PermissionsProvider>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route element={<ProtectedRoute check={isSuperAdmin}><Layout /></ProtectedRoute>}>
                <Route path="/" element={<Navigate to="/admin" replace />} />
                <Route path="/admin" element={<ProtectedRoute check={isSuperAdmin}><AdminDashboard /></ProtectedRoute>} />
                <Route path="/admin/billing-rates" element={<ProtectedRoute check={isSuperAdmin}><BillingRatesAdmin /></ProtectedRoute>} />
                <Route path="/admin/cellular" element={<ProtectedRoute check={isSuperAdmin}><CellularAdmin /></ProtectedRoute>} />
                <Route path="/admin/add-pdk" element={<Navigate to="/integrations?setup=pdk" replace />} />
                <Route path="/admin/inbox" element={<ProtectedRoute check={isSuperAdmin}><AdminInbox /></ProtectedRoute>} />
                <Route path="/admin/data" element={<ProtectedRoute check={isSuperAdmin}><AdminDataBrowser /></ProtectedRoute>} />
                <Route path="/admin/live-visitor" element={<ProtectedRoute check={isSuperAdmin}><LiveVisitorPages /></ProtectedRoute>} />
                <Route path="/admin/beta-visitor" element={<ProtectedRoute check={isSuperAdmin}><BetaVisitorDirectory /></ProtectedRoute>} />
                <Route path="/bridges" element={<ProtectedRoute check={isSuperAdmin}><IntrlockBridge /></ProtectedRoute>} />
                <Route path="/admin/intrlock-bridge" element={<ProtectedRoute check={isSuperAdmin}><IntrlockBridge /></ProtectedRoute>} />
                <Route path="/permissions" element={<ProtectedRoute check={isSuperAdmin}><Permissions /></ProtectedRoute>} />
                <Route path="/billing" element={<ProtectedRoute check={isSuperAdmin}><Billing /></ProtectedRoute>} />
                <Route path="/analytics" element={<ProtectedRoute check={isSuperAdmin}><Analytics /></ProtectedRoute>} />
                <Route path="/operations" element={<ProtectedRoute check={isSuperAdmin}><Operations /></ProtectedRoute>} />
                <Route path="/operations/metrics" element={<ProtectedRoute check={isSuperAdmin}><Metrics /></ProtectedRoute>} />
                <Route path="/metrics"            element={<ProtectedRoute check={isSuperAdmin}><Metrics /></ProtectedRoute>} />
                {/* Organization pages — mirror the web app so dealers,
                    customers, properties, people, guests, web users + mobile
                    users are all reachable from the categorized admin nav. */}
                <Route path="/integrators"          element={<ProtectedRoute check={isSuperAdmin}><Integrators /></ProtectedRoute>} />
                <Route path="/integrators/:id"      element={<ProtectedRoute check={isSuperAdmin}><IntegratorDetail /></ProtectedRoute>} />
                <Route path="/customers"            element={<ProtectedRoute check={isSuperAdmin}><Customers /></ProtectedRoute>} />
                <Route path="/customers/:id"        element={<ProtectedRoute check={isSuperAdmin}><CustomerDetail /></ProtectedRoute>} />
                <Route path="/properties"           element={<ProtectedRoute check={isSuperAdmin}><Properties /></ProtectedRoute>} />
                <Route path="/properties/:id"       element={<ProtectedRoute check={isSuperAdmin}><PropertyDetail /></ProtectedRoute>} />
                <Route path="/tenants"              element={<ProtectedRoute check={isSuperAdmin}><Tenants /></ProtectedRoute>} />
                <Route path="/tenants/:id"          element={<ProtectedRoute check={isSuperAdmin}><TenantDetail /></ProtectedRoute>} />
                <Route path="/guests"               element={<ProtectedRoute check={isSuperAdmin}><Guests /></ProtectedRoute>} />
                <Route path="/deliveries"           element={<ProtectedRoute check={isSuperAdmin}><Deliveries /></ProtectedRoute>} />
                <Route path="/vendors"              element={<ProtectedRoute check={isSuperAdmin}><Vendors /></ProtectedRoute>} />
                <Route path="/web-users"            element={<ProtectedRoute check={isSuperAdmin}><WebUsers /></ProtectedRoute>} />
                <Route path="/mobile-users"         element={<ProtectedRoute check={isSuperAdmin}><MobileUsers /></ProtectedRoute>} />
                {/* System pages */}
                <Route path="/devices"              element={<ProtectedRoute check={isSuperAdmin}><Devices /></ProtectedRoute>} />
                <Route path="/access-control"       element={<ProtectedRoute check={isSuperAdmin}><AccessControl /></ProtectedRoute>} />
                <Route path="/access-control/:id"   element={<ProtectedRoute check={isSuperAdmin}><AccessControlSystemDetail /></ProtectedRoute>} />
                <Route path="/cameras"              element={<ProtectedRoute check={isSuperAdmin}><Cameras /></ProtectedRoute>} />
                <Route path="/cameras/systems/:id"  element={<ProtectedRoute check={isSuperAdmin}><CameraSystemDetail /></ProtectedRoute>} />
                <Route path="/intercoms"            element={<ProtectedRoute check={isSuperAdmin}><Intercoms /></ProtectedRoute>} />
                <Route path="/rules"                element={<ProtectedRoute check={isSuperAdmin}><Rules /></ProtectedRoute>} />
                <Route path="/integrations"         element={<ProtectedRoute check={isSuperAdmin}><Integrations /></ProtectedRoute>} />
                <Route path="/push-sender"          element={<ProtectedRoute check={isSuperAdmin}><PushSender /></ProtectedRoute>} />
                <Route path="/notifications"        element={<ProtectedRoute check={isSuperAdmin}><NotificationsPage /></ProtectedRoute>} />
                {/* History */}
                <Route path="/call-history"         element={<ProtectedRoute check={isSuperAdmin}><CallHistory /></ProtectedRoute>} />
                <Route path="/device-history"       element={<ProtectedRoute check={isSuperAdmin}><DeviceHistory /></ProtectedRoute>} />
                <Route path="/event-history"        element={<ProtectedRoute check={isSuperAdmin}><EventHistory /></ProtectedRoute>} />
                <Route path="/reports"              element={<ProtectedRoute check={isSuperAdmin}><Reports /></ProtectedRoute>} />
                <Route path="/admin/stripe"         element={<ProtectedRoute check={isSuperAdmin}><StripePage /></ProtectedRoute>} />
                <Route path="/stripe"               element={<ProtectedRoute check={isSuperAdmin}><StripePage /></ProtectedRoute>} />
                <Route path="/security" element={<ProtectedRoute><Security /></ProtectedRoute>} />
                {/* Pre-launch shop pages — admin-only until they
                    ship to dealers / customers. */}
                {/* Sign Shop: allow admin + super_admin. Nav visibility on
                    shadow builds is hostname-gated in HeaderNav. */}
                <Route path="/sign-shop" element={<ProtectedRoute check={canAccessSignShop}><SignShop /></ProtectedRoute>} />
                <Route path="/sign-shop-orders" element={<ProtectedRoute check={canAccessSignShop}><SignShopOrders /></ProtectedRoute>} />
                <Route path="/vistaprint-shop" element={<ProtectedRoute check={isSuperAdmin}><VistaprintShop /></ProtectedRoute>} />
                <Route path="/accessories" element={<ProtectedRoute check={isSuperAdmin}><Accessories /></ProtectedRoute>} />
                <Route path="/accessory-shop-orders" element={<ProtectedRoute check={isSuperAdmin}><AccessoryShopOrders /></ProtectedRoute>} />
                <Route path="/device-profiles" element={<ProtectedRoute check={isSuperAdmin}><DeviceProfilesAdmin /></ProtectedRoute>} />
                <Route path="/claim-bridge" element={<ProtectedRoute><ClaimBridge /></ProtectedRoute>} />
                <Route path="*" element={<Navigate to="/admin" replace />} />
              </Route>
            </Routes>
          </PermissionsProvider>
        </BrowserRouter>
      </AuthProvider>
    );
  }

  return (
  <AuthProvider>
    <BrowserRouter>
      <AnalyticsTracker />
      <PermissionsProvider>
      <Routes>
        {/* ── Redirect old /visit/* URLs to visitor.intrlock.io ── */}
        <Route path="/visit/*" element={<VisitRedirect />} />

        {/* ── Tenant Video Call (no auth required - temporary workaround) ── */}
        <Route path="/tenant/call/:callId" element={<TenantVideoCall />} />

        {/* ── Login (public) ─────────────────────────────────────────── */}
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/login" element={<Login />} />

        {/* ── Authenticated Admin Routes (protected) ──────────────────── */}
        <Route element={<ProtectedRoute><Layout /></ProtectedRoute>}>
          <Route
            path="/dashboard"
            element={<ProtectedRoute><Dashboard /></ProtectedRoute>}
          />
          <Route
            path="/security"
            element={<ProtectedRoute><Security /></ProtectedRoute>}
          />
          <Route
            path="/claim-bridge"
            element={<ProtectedRoute><ClaimBridge /></ProtectedRoute>}
          />
          <Route
            path="/notifications"
            element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>}
          />
          <Route 
            path="/integrators" 
            element={<ProtectedRoute check={canViewIntegrators}><Integrators /></ProtectedRoute>} 
          />
          <Route 
            path="/integrators/:id" 
            element={<ProtectedRoute check={canViewIntegrators}><IntegratorDetail /></ProtectedRoute>} 
          />
          <Route 
            path="/customers" 
            element={<ProtectedRoute check={canViewCustomers}><Customers /></ProtectedRoute>} 
          />
          <Route 
            path="/customers/:id" 
            element={<ProtectedRoute check={canViewCustomers}><CustomerDetail /></ProtectedRoute>} 
          />
          <Route 
            path="/properties" 
            element={<ProtectedRoute check={canViewProperties}><Properties /></ProtectedRoute>} 
          />
          <Route 
            path="/properties/:id" 
            element={<ProtectedRoute check={canViewProperties}><PropertyDetail /></ProtectedRoute>} 
          />
          <Route 
            path="/tenants" 
            element={<ProtectedRoute check={canViewTenants}><Tenants /></ProtectedRoute>} 
          />
          <Route 
            path="/tenants/:id" 
            element={<ProtectedRoute check={canViewTenants}><TenantDetail /></ProtectedRoute>} 
          />
          <Route 
            path="/devices" 
            element={<ProtectedRoute check={canViewDevices}><Devices /></ProtectedRoute>} 
          />
          <Route
            path="/access-control"
            element={<ProtectedRoute check={canViewAccessControl}><AccessControl /></ProtectedRoute>}
          />
          <Route
            path="/access-control/:id"
            element={<ProtectedRoute check={canViewAccessControl}><AccessControlSystemDetail /></ProtectedRoute>}
          />
          <Route
            path="/cameras"
            element={<ProtectedRoute check={canViewCameras}><Cameras /></ProtectedRoute>}
          />
          <Route
            path="/cameras/systems/:id"
            element={<ProtectedRoute check={canViewCameras}><CameraSystemDetail /></ProtectedRoute>}
          />
          <Route
            path="/intercoms"
            element={<ProtectedRoute check={canViewIntercoms}><Intercoms /></ProtectedRoute>}
          />
          <Route
            path="/push-sender"
            element={<ProtectedRoute check={isSuperAdmin}><PushSender /></ProtectedRoute>}
          />
          <Route
            path="/settings"
            element={<ProtectedRoute check={canViewSettings}><Settings /></ProtectedRoute>} 
          />
          {/* /billing moved to admin.intrlock.io */}
          {/* Voice + Video are now tabs inside the consolidated Intercoms page.
              Legacy /voice keeps working but redirects to the Voice tab. */}
          <Route path="/voice" element={<Navigate to="/intercoms?tab=voice" replace />} />
          {/* Sign Shop: on CES app.intrlock.io they still live only on the admin
              build. On shadow.app.intrlock.io the Shop nav is exposed to
              super_admin + admin via HeaderNav's hostname gate, so the routes
              must be mounted here too or the catch-all bounces to /login. */}
          <Route path="/sign-shop" element={<ProtectedRoute check={canAccessSignShop}><SignShop /></ProtectedRoute>} />
          <Route path="/sign-shop-orders" element={<ProtectedRoute check={canAccessSignShop}><SignShopOrders /></ProtectedRoute>} />
          <Route
            path="/web-users"
            element={<ProtectedRoute check={canViewWebUsers}><WebUsers /></ProtectedRoute>} 
          />
          <Route 
            path="/mobile-users" 
            element={<ProtectedRoute check={canViewMobileUsers}><MobileUsers /></ProtectedRoute>} 
          />
          <Route
            path="/call-history"
            element={<ProtectedRoute check={canViewCallHistory}><CallHistory /></ProtectedRoute>}
          />
          <Route
            path="/device-history"
            element={<ProtectedRoute check={() => true}><DeviceHistory /></ProtectedRoute>}
          />
          <Route
            path="/reports"
            element={<ProtectedRoute check={() => true}><Reports /></ProtectedRoute>}
          />
          <Route
            path="/guests"
            element={<ProtectedRoute check={canViewGuests}><Guests /></ProtectedRoute>}
          />
          <Route
            path="/deliveries"
            element={<ProtectedRoute check={canViewGuests}><Deliveries /></ProtectedRoute>}
          />
          <Route
            path="/vendors"
            element={<ProtectedRoute check={canViewGuests}><Vendors /></ProtectedRoute>}
          />
          <Route
            path="/integrations"
            element={<ProtectedRoute check={canViewAccessControl}><Integrations /></ProtectedRoute>}
          />
          <Route
            path="/rules"
            element={<ProtectedRoute check={canViewAccessControl}><Rules /></ProtectedRoute>}
          />
          {/* All super-admin routes (/admin*, /bridges, /permissions,
              /analytics, /billing) live exclusively on
              admin.intrlock.io now. Hitting any of these on
              app.intrlock.io falls through to the catch-all below. */}
        </Route>

        <Route path="*" element={<Navigate to="/login" replace />} />
      </Routes>
      </PermissionsProvider>
    </BrowserRouter>
  </AuthProvider>
  );
};

export default App;
